import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { restApi } from "@/store/rest_api";
import type { AppDispatch } from "@/store/store";
import { useSocketSubscription } from "@/hooks/useSocketSubscription";
import { useSocketResync } from "@/hooks/useSocketResync";

export interface A2pStatusEvent {
  status: string;
  customerProfileStatus?: string;
  trustProductStatus?: string;
  brandStatus?: string;
  campaignStatus?: string;
  brandScore?: number;
  rejectionReason?: string;
}

const STATUS_MESSAGES: Record<string, { message: string; type: "success" | "error" | "info" }> = {
  PLATFORM_REVIEW: { message: "Your registration was submitted and is being reviewed by our team", type: "info" },
  PLATFORM_REJECTED: { message: "Your registration needs changes before we can submit it", type: "error" },
  PROFILE_APPROVED: { message: "Your business profile has been approved", type: "success" },
  PROFILE_REJECTED: { message: "Your business profile has been rejected", type: "error" },
  BRAND_APPROVED: { message: "Your brand registration has been approved", type: "success" },
  BRAND_FAILED: { message: "Your brand registration has failed", type: "error" },
  CAMPAIGN_FAILED: { message: "Your SMS campaign has failed", type: "error" },
  COMPLETED: { message: "A2P registration is complete! You can now send SMS", type: "success" },
};

export function useA2pStatusSocket() {
  const dispatch = useDispatch<AppDispatch>();

  useSocketSubscription("/user/queue/a2p-status", (message) => {
    try {
      const event: A2pStatusEvent = JSON.parse(message.body);
      dispatch(restApi.util.invalidateTags(["A2pRegistration", "SmsEligibility", "Notifications"]));

      const statusInfo = STATUS_MESSAGES[event.status];
      if (statusInfo && !message.replayed) {
        const statusMessage = event.rejectionReason
          ? `${statusInfo.message}: ${event.rejectionReason}`
          : statusInfo.message;

        toast[statusInfo.type](statusMessage);
      }
    } catch (error) {
      console.error("Failed to parse A2P status event", error);
    }
  });

  useSocketResync((resync) => {
    if (!resync.fullRefresh) {
      dispatch(restApi.util.invalidateTags(["A2pRegistration", "SmsEligibility"]));
    }
  });
}
